import React, { useState, useEffect } from 'react';
import axios from '../config/api';
import { toast } from 'react-toastify';
import { useAuth } from '../context/AuthContext';
import { usePageContext } from '../context/PageContext';

const PaymentHistory = () => {
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedRecipient, setSelectedRecipient] = useState('');
  const [recipients, setRecipients] = useState([]);
  const { hasPermission } = useAuth();
  const { setPageTitle } = usePageContext();

  useEffect(() => {
    setPageTitle('Payment History');
    fetchPayments();
    fetchRecipients();
  }, [setPageTitle]);

  const fetchPayments = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/payment');
      setPayments(response.data);
    } catch (error) {
      toast.error('Error fetching payments');
      setPayments([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchRecipients = async () => {
    try {
      const response = await axios.get('/api/expense-recipient');
      setRecipients(response.data);
    } catch (error) {
      console.error('Error fetching recipients');
    }
  };

  const filteredPayments = selectedRecipient 
    ? payments.filter(p => p.recipient._id === selectedRecipient)
    : payments;

  const totalPaid = filteredPayments.reduce((sum, payment) => sum + payment.amount, 0);

  if (!hasPermission('expense', 'read')) {
    return <div className="alert alert-danger">Access denied</div>;
  }

  return (
    <div className="container-fluid">
      {/* Page Header */}
      <div className="row mb-4">
        <div className="col-12">
          <div className="card" style={{background: 'linear-gradient(135deg, #059669 0%, #10b981 100%)'}}>
            <div className="card-body text-white">
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <h4 className="mb-2">💳 Payment History</h4>
                  <p className="mb-0 opacity-75">Complete record of all payments made to expense recipients</p>
                  <small className="opacity-75">Track payment transactions, methods, and recipient details</small>
                </div>
                <div className="text-end">
                  <div className="h4 mb-0">${totalPaid.toFixed(2)}</div>
                  <small className="opacity-75">{filteredPayments.length} payments recorded</small>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="row mb-4">
        <div className="col-md-3">
          <div className="card border-success">
            <div className="card-body text-center">
              <div className="text-success mb-2" style={{fontSize: '2rem'}}>💰</div>
              <h6 className="text-success">Total Paid</h6>
              <h4 className="mb-0">${totalPaid.toFixed(2)}</h4>
            </div>
          </div>
        </div>
        <div className="col-md-3">
          <div className="card border-info">
            <div className="card-body text-center">
              <div className="text-info mb-2" style={{fontSize: '2rem'}}>📄</div>
              <h6 className="text-info">Total Payments</h6>
              <h4 className="mb-0">{filteredPayments.length}</h4>
            </div>
          </div>
        </div>
        <div className="col-md-3">
          <div className="card border-primary">
            <div className="card-body text-center">
              <div className="text-primary mb-2" style={{fontSize: '2rem'}}>👥</div>
              <h6 className="text-primary">Recipients</h6>
              <h4 className="mb-0">{new Set(filteredPayments.map(p => p.recipient._id)).size}</h4>
            </div>
          </div>
        </div>
        <div className="col-md-3">
          <div className="card border-warning">
            <div className="card-body text-center">
              <div className="text-warning mb-2" style={{fontSize: '2rem'}}>📅</div>
              <h6 className="text-warning">This Month</h6>
              <h4 className="mb-0">
                ${filteredPayments
                  .filter(p => new Date(p.paymentDate).getMonth() === new Date().getMonth())
                  .reduce((sum, p) => sum + p.amount, 0)
                  .toFixed(2)}
              </h4>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="card mb-4">
        <div className="card-header">
          <h6 className="mb-0">🔍 Filter Payments</h6>
        </div>
        <div className="card-body">
          <div className="row g-3">
            <div className="col-md-4">
              <label className="form-label">Filter by Recipient</label>
              <select
                className="form-select"
                value={selectedRecipient}
                onChange={(e) => setSelectedRecipient(e.target.value)}
              >
                <option value="">All Recipients ({payments.length} payments)</option>
                {recipients.map(recipient => {
                  const recipientPayments = payments.filter(p => p.recipient._id === recipient._id);
                  return (
                    <option key={recipient._id} value={recipient._id}>
                      {recipient.name} ({recipientPayments.length} payments - ${recipientPayments.reduce((sum, p) => sum + p.amount, 0).toFixed(2)})
                    </option>
                  );
                })}
              </select>
            </div>
            <div className="col-md-8">
              <label className="form-label">Quick Info</label>
              <div className="form-control-plaintext">
                {selectedRecipient ? (
                  <span className="text-muted">
                    Showing {filteredPayments.length} payments totaling ${totalPaid.toFixed(2)} for selected recipient
                  </span>
                ) : (
                  <span className="text-muted">
                    Showing all {payments.length} payments across {recipients.length} recipients
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-2 text-muted">Loading payment history...</p>
        </div>
      ) : filteredPayments.length === 0 ? (
        <div className="card">
          <div className="card-body text-center py-5">
            <div style={{ fontSize: '4rem', opacity: 0.3 }}>💳</div>
            <h5 className="text-muted mt-3">No payments found</h5>
            <p className="text-muted mb-4">No payment transactions match your current filters.</p>
            <div className="alert alert-info text-start">
              <h6><i className="fas fa-info-circle me-2"></i>Payment Process:</h6>
              <ol className="mb-0">
                <li>Expenses must be approved first</li>
                <li>Approved expenses create due amounts</li>
                <li>Use "Expense Recipients" page to make payments</li>
                <li>Payments will appear here once processed</li>
              </ol>
            </div>
          </div>
        </div>
      ) : (
        <div className="card">
          <div className="card-header">
            <div className="d-flex justify-content-between align-items-center">
              <h6 className="mb-0">Payment Transactions</h6>
              <small className="text-muted">{filteredPayments.length} payments</small>
            </div>
          </div>
          <div className="table-responsive">
            <table className="table table-hover mb-0">
              <thead className="table-light">
                <tr>
                  <th>Date & Time</th>
                  <th>Recipient Details</th>
                  <th>Amount</th>
                  <th>Payment Method</th>
                  <th>Reference</th>
                  <th>Processed By</th>
                  <th>Notes</th>
                </tr>
              </thead>
              <tbody>
                {filteredPayments.map(payment => (
                  <tr key={payment._id}>
                    <td>
                      <div className="fw-semibold">{new Date(payment.paymentDate).toLocaleDateString()}</div>
                      <small className="text-muted">{new Date(payment.paymentDate).toLocaleTimeString()}</small>
                    </td>
                    <td>
                      <div className="d-flex align-items-center">
                        <div className="avatar-circle me-3" style={{ width: '40px', height: '40px' }}>
                          {payment.recipient.name.charAt(0).toUpperCase()}
                        </div>
                        <div>
                          <div className="fw-semibold">{payment.recipient.name}</div>
                          <span className={`badge badge-sm ${
                            payment.recipient.type === 'employee' ? 'bg-primary' :
                            payment.recipient.type === 'vendor' ? 'bg-success' :
                            payment.recipient.type === 'contractor' ? 'bg-info' :
                            payment.recipient.type === 'partner' ? 'bg-warning' : 'bg-secondary'
                          }`}>
                            {payment.recipient.type}
                          </span>
                        </div>
                      </div>
                    </td>
                    <td>
                      <div className="fw-bold text-success" style={{fontSize: '1.1rem'}}>${payment.amount.toFixed(2)}</div>
                      <small className="text-muted">{payment.currency}</small>
                    </td>
                    <td>
                      <span className={`badge ${
                        payment.paymentMethod === 'bank_transfer' ? 'bg-primary' :
                        payment.paymentMethod === 'check' ? 'bg-info' :
                        payment.paymentMethod === 'cash' ? 'bg-success' : 'bg-warning'
                      }`}>
                        {payment.paymentMethod.replace('_', ' ').toUpperCase()}
                      </span>
                    </td>
                    <td>
                      {payment.reference ? (
                        <code className="small bg-light p-1 rounded">{payment.reference}</code>
                      ) : (
                        <span className="text-muted">No reference</span>
                      )}
                    </td>
                    <td>
                      <div className="d-flex align-items-center">
                        <i className="fas fa-user-circle text-muted me-2"></i>
                        <span>{payment.paidBy?.name || 'Unknown'}</span>
                      </div>
                    </td>
                    <td>
                      {payment.notes ? (
                        <span className="small text-muted" title={payment.notes}>
                          {payment.notes.length > 30 ? payment.notes.substring(0, 30) + '...' : payment.notes}
                        </span>
                      ) : (
                        <span className="text-muted">-</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default PaymentHistory;